import { useDebounce, useI18N, useResponsive } from "@sundae/react-hooks";
import { Cross1Icon, Input, Loader, MagnifyingGlassIcon } from "@sundae/ui-toolkit";
import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import cx from "classnames";
import Fuse from "fuse.js";
import { FC, ReactNode, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

import { SundaeUtils } from "@sundaeswap/core/utilities";
import {
  ASSETS_SEARCH_OPTIONS,
  ASSETS_SELECT_FLOW_SEARCH_OPTIONS,
} from "../../constants/fuse.constants";
import { testIds } from "../../constants/testIds.constants";
import { usePoolsBySearch } from "../../gql/hooks/pools.query";
import { useAdaBalance } from "../../hooks/useAdaBalance";
import { useIsWalletConnected } from "../../hooks/useIsWalletConnected";
import { ExchangeActions, useExchangeContext } from "../../stores/ExchangeContext";
import { IAssetMetaData } from "../../types/Asset.types";
import { processAsset } from "../../utils/assets-search.utils";
import AssetSelectItem from "../AssetSelectItem";
import AssetSelectItemSkeleton from "../AssetSelectItem/AssetSelectItem.skeleton";
import { INPUT_DEBOUNCE_IN_MS } from "../../constants/page.constants";

export interface IAssetSelectFlow {
  activeAsset?: AssetAmount<IAssetMetaData> | null;
  assets: IMappedAsset<IAssetMetaData>[];
  autoFocusSearch?: boolean;
  isFetched?: boolean;
  isLoading?: boolean;
  onAssetSelect?(asset: IMappedAsset<IAssetMetaData>): void;
  renderNoSearchResults?: ReactNode;
  searchPlaceholder?: string;
}

const AssetSelectFlow: FC<IAssetSelectFlow> = ({
  activeAsset,
  assets,
  autoFocusSearch,
  isFetched = true,
  isLoading = false,
  onAssetSelect,
  renderNoSearchResults,
  searchPlaceholder,
}) => {
  const { loaded } = useAdaBalance();
  const isWalletConnected = useIsWalletConnected();
  const { currentLocale } = useI18N();
  const responsive = useResponsive();
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchValue = useDebounce(searchInput, { wait: INPUT_DEBOUNCE_IN_MS });
  const { dispatch: exchangeContextDispatch } = useExchangeContext();
  const [previousSearchedAssets, setPreviousSearchedAssets] = useState<
    IMappedAsset<IAssetMetaData>[]
  >([]);
  const {
    data: poolsSearched,
    isFetched: isFetchedPoolsSearch,
    isFetching: isFetchingPoolsSearched,
    isRefetching: isRefetchingPoolsSearched,
  } = usePoolsBySearch(debouncedSearchValue);

  const isLargeScreen = useMemo(
    () => responsive.lg || responsive.xl || responsive.xxl,
    [responsive]
  );

  /* -------------------------------------------------------------------------------------------------
   * The Fuse instance for filtered Assets.
   * This is the default list that gets rendered when the user is searching for assets.
   * By default, the ExchangeContext returns the first 50 assets from the `useInputOutputAssets` hook.
   * We are filtering those assets by the search input and return the filtered list.
   * -----------------------------------------------------------------------------------------------*/
  const fuse = useMemo(() => new Fuse(assets, ASSETS_SEARCH_OPTIONS), [assets]);

  /* -------------------------------------------------------------------------------------------------
   * The Fuse instance for searched Assets.
   * This is the list of assets that gets returned based on the result of the `usePoolsBySearch` hook.
   * The `usePoolsBySearch` hook returns a list of pools that match the search input.
   * -----------------------------------------------------------------------------------------------*/
  const poolfuse = useMemo(
    () =>
      new Fuse(
        poolsSearched?.stats ?? [],
        isWalletConnected ? ASSETS_SELECT_FLOW_SEARCH_OPTIONS : ASSETS_SEARCH_OPTIONS
      ),
    [isWalletConnected, poolsSearched?.stats]
  );

  /* -------------------------------------------------------------------------------------------------
   * This is the filtered list of _local_ assets based on the search input.
   * -----------------------------------------------------------------------------------------------*/
  const filteredAssets = useMemo(() => {
    if (!searchInput) return assets;
    return fuse.search(searchInput).map(({ item }) => item);
  }, [assets, searchInput, fuse]);

  /* -------------------------------------------------------------------------------------------------
   * This is the filtered list of _searched_ assets based on the search input.
   * This list is only used when the user is searching for assets.
   * -----------------------------------------------------------------------------------------------*/
  const searchedAssets = useMemo(() => {
    if (
      // If there is no search input, return all assets and reset previous searched assets --> this will prevent a flickering of showing previous searched assets when entering a new search input.
      !debouncedSearchValue
    ) {
      setPreviousSearchedAssets([]);
      return assets;
    }

    if (filteredAssets.length && isFetchingPoolsSearched) {
      return filteredAssets;
    }

    // If previous search results are available and we're actively searching pools, return the previous results until the new ones are in.
    if (previousSearchedAssets.length && isFetchingPoolsSearched) {
      return previousSearchedAssets;
    }

    const searchInput = debouncedSearchValue.toLowerCase();
    const filteredSearchResults = poolfuse
      .search(debouncedSearchValue)
      .reduce((acc: IMappedAsset<IAssetMetaData>[], { item }) => {
        const { assetA, assetB } = item;
        processAsset(acc, searchInput, assetA, assets);
        processAsset(acc, searchInput, assetB, assets);
        return acc;
      }, []);
    setPreviousSearchedAssets(filteredSearchResults);

    return filteredSearchResults;
  }, [assets, isFetchingPoolsSearched, poolsSearched?.stats, debouncedSearchValue]);

  /* -------------------------------------------------------------------------------------------------
   * This is the list of assets that _actually_ gets rendered.
   * If a search is active, we want to show the filtered assets first.
   * If the search is done, we want to show the searched assets.
   * If a new search is happening while currently rendering already searched/filtered assets, we want to show the previous searched assets until the new ones are in.
   * If the search did not return any results but the `filteredAssets` are available, simply return them. The most common use case for that is if the user has only entered a single character in the search input.
   * Fallback is to render the default list of assets.
   * -----------------------------------------------------------------------------------------------*/
  const renderedAssets = useMemo(() => {
    if ((filteredAssets.length && isRefetchingPoolsSearched) || !isFetchedPoolsSearch) {
      return filteredAssets;
    }
    if (isFetchedPoolsSearch && searchedAssets.length) {
      return searchedAssets;
    }
    if (previousSearchedAssets.length && debouncedSearchValue) {
      return previousSearchedAssets;
    }
    return filteredAssets;
  }, [
    debouncedSearchValue,
    filteredAssets,
    isFetchedPoolsSearch,
    isRefetchingPoolsSearched,
    previousSearchedAssets,
    searchedAssets,
  ]);

  /* -------------------------------------------------------------------------------------------------
   * This is the content that gets rendered in the AssetSelectFlow.
   * If we are currently loading the balance or assets, we want to show a skeleton.
   * As soon as loading is done, we want to show the filtered assets.
   * If there are no filtered assets, we want to show the `renderNoSearchResults` prop.
   * -----------------------------------------------------------------------------------------------*/
  const renderContent = () => {
    if (
      (!isFetched && isWalletConnected) ||
      (!loaded && isWalletConnected) ||
      (!renderedAssets.length && !isFetchedPoolsSearch)
    ) {
      return [...Array(3).keys()].map((item) => <AssetSelectItemSkeleton key={item} />);
    }

    if (renderedAssets.length) {
      return renderedAssets.map((asset, index) => (
        <AssetSelectItem
          key={`${asset.metadata.assetId}-${index}`}
          isActiveAsset={
            (SundaeUtils.isAdaAsset(activeAsset?.metadata) &&
              SundaeUtils.isAdaAsset(asset.metadata)) ||
            activeAsset?.metadata.assetId === asset.metadata.assetId
          }
          asset={asset}
          currentLocale={currentLocale}
          isLargeScreen={isLargeScreen}
          onAssetSelect={onAssetSelect}
        />
      ));
    }

    return renderNoSearchResults;
  };

  return (
    <>
      {isWalletConnected && (
        <div className="relative w-full max-w-lg flex-1">
          <Input
            data-testid={testIds.assetSelectFlow.input}
            autoFocus={autoFocusSearch}
            className="text-sm"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={searchPlaceholder}
            startAdornment={<MagnifyingGlassIcon width={20} height={20} />}
            value={searchInput}
            {...(searchInput &&
              !isLoading &&
              isWalletConnected &&
              !isFetchingPoolsSearched && {
                endAdornment: (
                  <Cross1Icon
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setSearchInput("")}
                  />
                ),
              })}
          />
          <Loader
            size="xsmall"
            className={cx("!absolute right-6 top-1/2 -translate-y-1/2", {
              hidden: (!isLoading && !isFetchingPoolsSearched) || !isWalletConnected,
            })}
          />
        </div>
      )}

      <InfiniteScroll
        className="flex max-h-[20rem] flex-col gap-2 xxl:max-h-[28rem]"
        dataLength={searchedAssets?.length}
        hasMore
        height={504}
        loader={null}
        next={() => {
          ExchangeActions.increasePageAssetList(exchangeContextDispatch);
        }}
      >
        <div
          aria-labelledby="asset-select"
          className={cx("flex w-full flex-col items-center gap-2", {
            "justify-start": (!isLoading && !isFetchingPoolsSearched) || !isWalletConnected,
          })}
        >
          {renderContent()}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default AssetSelectFlow;
